import React, { useState, useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  InputAdornment,
  TextField,
  Divider,
  CircularProgress,
  Button,
  Tooltip,
  useMediaQuery,
  useTheme
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const ChatSidebar = ({
  contacts,
  onSelectContact,
  selectedContact,
  currentTab, // Nova prop para identificar a aba atual
  renderContactMeta,
  headerAction,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [visibleContacts, setVisibleContacts] = useState(12);
  const [copiedContactKey, setCopiedContactKey] = useState(null);
  const copiedTimeoutRef = React.useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Filtragem com loading
  useEffect(() => {
    setLoading(true);
    const timeout = setTimeout(() => {
      const filtered = contacts.filter((contact) => {
        const nome = contact.nome || "";
        const telefone = contact.whatsAppId || "";

        // Verifica se corresponde ao termo de busca
        const matchesSearch = nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
          telefone.toLowerCase().includes(searchTerm.toLowerCase());

        // Se não corresponde ao termo de busca, exclui
        if (!matchesSearch) return false;

        return true;
      });

      setFilteredContacts(filtered);
      setLoading(false);
      // Reset visible contacts quando a busca muda
      setVisibleContacts(12);
    }, 10);

    return () => clearTimeout(timeout);
  }, [searchTerm, contacts ]);

  // Função para lidar com "Ver todos" com loading
  const handleViewAll = async () => {
    setLoadingAll(true);
    // Simula carregamento de todos os contatos
    await new Promise(resolve => setTimeout(resolve, 2000));
    setVisibleContacts(filteredContacts.length);
    setLoadingAll(false);
  };

  const copyToClipboard = async (text) => {
    if (!text) return false;
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        return true;
      } catch {
        return false;
      }
    }
  };

  const handleContactContextMenu = async (event, contact, contactKey) => {
    event.preventDefault();
    event.stopPropagation();
    const numberToCopy = contact?.whatsAppId || contact?.telefone;
    const success = await copyToClipboard(numberToCopy);
    if (!success) return;

    setCopiedContactKey(contactKey);
    if (copiedTimeoutRef.current) {
      clearTimeout(copiedTimeoutRef.current);
    }
    copiedTimeoutRef.current = setTimeout(() => {
      setCopiedContactKey(null);
    }, 1200);
  };

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 340 },
        bgcolor: 'background.paper',
        maxHeight: '100%',
        height: '100%',
        borderRight: { xs: 'none', md: '1px solid' },
        borderColor: 'divider',
        borderRadius: 0,
        display: "flex",
        flexDirection: "column",
        boxShadow: "none",
        overflowX: "hidden",
        boxSizing: "border-box",
        minWidth: 0,
        flexShrink: 0,
      }}
    >
        <Box
          sx={{
            color: "text.primary",
            p: { xs: 1.5, md: 2 },
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#f0f2f5",
            borderRadius: 0,
            fontSize: { xs: "1rem", md: "1.25rem" },
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontSize: { xs: "1rem", md: "1.25rem" }, fontWeight: 700 }}>Conversas</Typography>
          {headerAction}
        </Box>

      {/* Campo de busca */}
      <Box sx={{ px: { xs: 1.5, md: 2 }, mb: 1 }}>
        <TextField
          placeholder="Buscar por nome ou telefone"
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            mt: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: 1.5,
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "#fff",
              color: "text.primary",
              "& fieldset": {
                borderColor: "divider",
              },
              "&:hover fieldset": {
                borderColor: "primary.main",
              },
            },
            "& .MuiInputBase-input": {
              color: "text.primary",
            },
          }}
        />
      </Box>

      <Divider />

      {/* Lista de contatos com loading */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          height: "auto",
          overflowY: "auto",
          overflowX: "hidden",
          mt: 1,
          width: "100%",
          boxSizing: "border-box",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "divider",
            borderRadius: "3px",
          },
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              py: 4,
            }}
          >
            <CircularProgress />
          </Box>
        ) : contacts.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              py: 4,
            }}
          >
            <Typography sx={{ color: "text.secondary" }}>Carregando contatos...</Typography>
          </Box>
        ) : loadingAll ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              py: 4,
              gap: 2,
            }}
          >
            <CircularProgress size={40} />
            <Typography sx={{ color: "text.secondary", textAlign: "center" }}>
              Carregando todos os contatos...
            </Typography>
            <Typography sx={{ color: "text.disabled", fontSize: "0.8rem", textAlign: "center" }}>
              Aguarde enquanto carregamos {filteredContacts.length} contatos
            </Typography>
          </Box>
        ) : (
          <>
            <List>
              {filteredContacts.length > 0 ? (
                filteredContacts.slice(0, visibleContacts).map((contact, index) => {
                  const contactKey = contact.whatsAppId || contact.telefone || index;
                  const selectedKey = selectedContact?.whatsAppId || selectedContact?.telefone;
                  const isSelected = Boolean(selectedKey && selectedKey === contactKey);
                  return (
                  <React.Fragment key={contactKey}>
                    <ListItem disablePadding>
                      <Tooltip
                        title="Copiado"
                        open={copiedContactKey === contactKey}
                        placement="top"
                        arrow
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                      >
                        <ListItemButton
                          onClick={() => onSelectContact(contact)}
                          onContextMenu={(event) => handleContactContextMenu(event, contact, contactKey)}
                          sx={{
                            bgcolor: isSelected ? "action.selected" : "transparent",
                            transition: "0.3s",
                            py: 1.2,
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                  {contact.nome || "Sem Nome"}
                                </Typography >
                                {(contact.papel || contact.tipo) && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      bgcolor: contact.tipo === 'Gestor'
                                        ? 'rgba(25, 118, 210, 0.12)'
                                        : 'rgba(245, 124, 0, 0.12)',
                                      color: contact.tipo === 'Gestor' ? 'primary.main' : 'warning.main',
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      fontSize: '0.7rem',
                                      fontWeight: 'medium',
                                      textAlign: 'right',
                                    }}
                                  >
                                    {contact.tipo === 'Gestor' ? 'G' : 'C'}
                                  </Typography>
                                )}
                                {renderContactMeta?.(contact)}
                              </Box>
                            }
                            secondary={contact.whatsAppId || contact.telefone}
                            primaryTypographyProps={{
                              color: "text.primary",
                            }}
                            secondaryTypographyProps={{
                              color: "text.secondary",
                              fontSize: "0.9rem",
                            }}
                          />
                        </ListItemButton>
                      </Tooltip>
                    </ListItem>
                    {index < Math.min(visibleContacts, filteredContacts.length) - 1 && <Divider />}
                  </React.Fragment>
                  );
                })
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography sx={{ color: "text.secondary" }}>
                    {searchTerm ? `Nenhum contato encontrado para "${searchTerm}"` : "Nenhum contato encontrado."}
                  </Typography>
                </Box>
              )}
            </List>

            {/* Botões de paginação */}
            {filteredContacts.length > 12 && (
              <Box sx={{ textAlign: 'center', p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>

                  {/* Botão "Ver mais" versão Mobile */}
                  {(visibleContacts < filteredContacts.length && isMobile) && (
                    <Button
                      onClick={() => setVisibleContacts(visibleContacts + 50)}
                      variant="outlined"
                      size="small"
                      sx={{
                        px: 0.5, py: 0.5,
                        border: '1px solid #667eea',
                        borderRadius: '100%',
                        background: 'rgba(102, 126, 234, 0.08)',
                        color: '#667eea',
                        fontWeight: 1000,
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        minWidth: '40px',
                        minHeight: '40px',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
                          background: 'rgba(102, 126, 234, 0.12)',
                        }
                      }}
                    >
                      +
                    </Button>
                  )}

                  {/* Botão "Ver mais" versão Desktop */}
                  {(visibleContacts < filteredContacts.length && !isMobile) && (
                    <Button
                      onClick={() => setVisibleContacts(visibleContacts + 50)}
                      variant="outlined"
                      size="small"
                      sx={{
                        px: 2, py: 0.5,
                        border: '1px solid #667eea',
                        borderRadius: '16px',
                        background: 'rgba(102, 126, 234, 0.08)',
                        color: '#667eea',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        minWidth: '80px',
                        minHeight: '28px',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
                          background: 'rgba(102, 126, 234, 0.12)',
                        },
                        '&:disabled': {
                          opacity: 0.6,
                        }
                      }}
                    >
                      Ver mais
                    </Button>
                  )}

                  {/* Botão "Ver menos" versão Mobile */}
                  {(visibleContacts > 12 && isMobile) && (
                    <Button
                      onClick={() => setVisibleContacts(12)}
                      variant="outlined"
                      size="small"
                      sx={{
                        px: 0.5, py: 0.5,
                        border: '1px solid #e53935',
                        borderRadius: '100%',
                        background: 'rgba(229, 57, 53, 0.08)',
                        color: '#e53935',
                        fontWeight: 1000,
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        minWidth: '40px',
                        minHeight: '40px',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(229, 57, 53, 0.2)',
                          background: 'rgba(229, 57, 53, 0.12)',
                        }
                      }}
                    >
                      −
                    </Button>
                  )}

                  {/* Botão "Ver menos" versão Desktop */}
                  {(visibleContacts > 12 && !isMobile) && (
                    <Button
                      onClick={() => setVisibleContacts(12)}
                      variant="outlined"
                      size="small"
                      sx={{
                        px: 2, py: 0.5,
                        border: '1px solid #e53935',
                        borderRadius: '16px',
                        background: 'rgba(229, 57, 53, 0.08)',
                        color: '#e53935',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        minWidth: '80px',
                        minHeight: '28px',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(229, 57, 53, 0.2)',
                          background: 'rgba(229, 57, 53, 0.12)',
                        },
                        '&:disabled': {
                          opacity: 0.6,
                        }
                      }}
                    >
                      Ver menos
                    </Button>
                  )}

                  {/* Botão "Ver todos" */}
                  {visibleContacts < filteredContacts.length && (
                    <Button
                      onClick={handleViewAll}
                      variant="outlined"
                      size="small"
                      disabled={loadingAll}
                      sx={{
                        px: 2, py: 0.5,
                        border: '1px solid #43a047',
                        borderRadius: '16px',
                        background: 'rgba(76, 175, 80, 0.08)',
                        color: '#43a047',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        minWidth: '80px',
                        minHeight: '28px',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(76, 175, 80, 0.2)',
                          background: 'rgba(76, 175, 80, 0.12)',
                        },
                        '&:disabled': {
                          opacity: 0.6,
                        }
                      }}
                    >
                      {loadingAll ? 'Carregando...' : 'Ver todos'}
                    </Button>
                  )}
                </Box>

                {/* Indicador de quantidade */}
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    mt: 1,
                    display: 'block',
                    fontSize: '0.75rem'
                  }}
                >
                  Mostrando {Math.min(visibleContacts, filteredContacts.length)} de {filteredContacts.length} contatos
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default ChatSidebar;
